import React, { useState } from 'react';
import { useStableDisclosure } from '../../utils/stableDisclosure';
import styled from 'styled-components';
import LanguageIcon from '@mui/icons-material/Language';
import GavelIcon from '@mui/icons-material/Gavel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PlaceIcon from '@mui/icons-material/Place';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SearchResultsContentItem } from '../../shared/interfaces';

interface Props {
  data: SearchResultsContentItem;
}

const TOOL_ICON: Record<SearchResultsContentItem['tool'], React.ReactNode> = {
  web_search: <LanguageIcon style={{ fontSize: 15 }} />,
  buscar_jurisprudencia: <GavelIcon style={{ fontSize: 15 }} />,
  buscar_legislacao: <MenuBookIcon style={{ fontSize: 15 }} />,
  buscar_lugares: <PlaceIcon style={{ fontSize: 15 }} />,
};

// Result cards for the search tools (web / jurisprudência / legislação) —
// mirrors the "Pesquisou na web" result panel: a collapsible header with the
// query + hit count, expanding to per-result cards (favicon/icon, title,
// source, optional snippet). Collapsed by default; click the header to expand.
const SearchResultsBlock: React.FC<Props> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  // Recolher um corpo alto não pode deixar o navegador cortar a rolagem.
  const { disclosureRef, toggleDisclosure } = useStableDisclosure(expanded, setExpanded);
  const results = data.results || [];
  if (results.length === 0) return null;
  const count = data.count || results.length;
  const isWeb = data.tool === 'web_search';

  return (
    <Row ref={disclosureRef} data-testid="search-results-block" data-download-exclude>
      <Header onClick={toggleDisclosure}>
        <IconWrap>{TOOL_ICON[data.tool]}</IconWrap>
        <Query title={data.query}>{data.query || 'resultados'}</Query>
        <Count>{count} {count === 1 ? 'resultado' : 'resultados'}</Count>
        <ChevronWrap>
          {expanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
        </ChevronWrap>
      </Header>

      {expanded && (
        <Cards>
          {results.map((r, i) => {
            const card = (
              <Card key={i} as={r.url ? 'a' : 'div'}
                {...(r.url ? { href: r.url, target: '_blank', rel: 'noopener noreferrer' } : {})}>
                <CardIcon>
                  {isWeb && r.source ? (
                    <Favicon
                      src={`https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(r.source)}`}
                      alt=""
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                    />
                  ) : (
                    TOOL_ICON[data.tool]
                  )}
                </CardIcon>
                <CardBody title={r.snippet || undefined}>
                  <CardTitle>{r.title}</CardTitle>
                  {r.source ? <CardSource>{r.source}</CardSource> : null}
                </CardBody>
              </Card>
            );
            return card;
          })}
        </Cards>
      )}
    </Row>
  );
};

export default SearchResultsBlock;

// ============ Styled Components ============

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 0;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  font-size: 0.88rem;
  line-height: 1.2;
  align-self: flex-start;
  border-radius: 6px;
  user-select: none;
  max-width: 100%;
  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')};
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.85;
  flex-shrink: 0;
  margin-left: -3px;
`;

const Query = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 420px;
`;

const Count = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 9px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
  flex-shrink: 0;
`;

const Cards = styled.div`
  margin: 4px 0 8px 14px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  max-height: 300px;
  overflow-y: auto;
  border-left: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  padding-left: 12px;
`;

const Card = styled.div`
  /* Compact single-line row: favicon + title + domain. The old two-line
     cards made a 10-result search taller than the viewport. */
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 8px;
  border-radius: 6px;
  text-decoration: none;
  color: inherit;
  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')};
  }
`;

const CardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  opacity: 0.8;
  flex-shrink: 0;
`;

const Favicon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 3px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
`;

const CardTitle = styled.span`
  font-size: 0.82rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
`;

const CardSnippet = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const CardSource = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  opacity: 0.6;
  white-space: nowrap;
  flex-shrink: 0;
`;
